import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import './MagazineViewer.css';

// Each PNG is already a full open-book spread (~2381x842 ≈ 2.83:1)
const PAGE_ASPECT = 2381 / 842;
const TOTAL_PAGES = 42;

// Render pages in reverse so the flip engine's LEFT-edge turn (= flipPrev)
// advances through the portfolio 1→2→3… (RTL-style), without CSS mirroring.
const PAGE_FILES = Array.from({ length: TOTAL_PAGES }, (_, i) => TOTAL_PAGES - i); // [42..1]
const START_INDEX = TOTAL_PAGES - 1; // index of page-1.png

function fileToIndex(fileNum) {
  return TOTAL_PAGES - fileNum; // page 1 → index 41
}

function indexToFile(index) {
  return TOTAL_PAGES - index; // index 41 → page 1
}

function getBookSize() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const isMobile = vw <= 768;
  const maxW = Math.min(vw * (isMobile ? 0.96 : 0.94), isMobile ? 920 : 1280);
  const maxH = vh - (isMobile ? 160 : 150);
  let width = maxW;
  let height = width / PAGE_ASPECT;
  if (height > maxH) {
    height = maxH;
    width = height * PAGE_ASPECT;
  }
  return {
    width: Math.max(280, Math.round(width)),
    height: Math.max(100, Math.round(height)),
    isMobile,
  };
}

function MagazineViewer() {
  // currentFile = 1..42 (what the user sees in the indicator)
  const [currentFile, setCurrentFile] = useState(1);
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [bookSize, setBookSize] = useState(() => getBookSize());
  const bookRef = useRef(null);

  const getPageFlip = useCallback(() => {
    try {
      return bookRef.current?.pageFlip?.() || null;
    } catch {
      return null;
    }
  }, []);

  const onFlip = useCallback((e) => {
    setCurrentFile(indexToFile(e.data));
  }, []);

  const onInit = useCallback(() => {
    const pageFlip = bookRef.current?.pageFlip?.();
    if (!pageFlip) return;
    try {
      if (typeof pageFlip.turnToPage === 'function') {
        pageFlip.turnToPage(START_INDEX);
      }
    } catch (_) {
      /* ignore */
    }
    setCurrentFile(1);
  }, []);

  const navigateToFile = useCallback(
    (fileNum) => {
      if (fileNum < 1 || fileNum > TOTAL_PAGES) return;
      setShowThumbnails(false);
      const pageFlip = getPageFlip();
      const index = fileToIndex(fileNum);
      try {
        if (pageFlip?.turnToPage) {
          pageFlip.turnToPage(index);
        } else if (pageFlip?.flip) {
          pageFlip.flip(index);
        }
        setCurrentFile(fileNum);
      } catch (error) {
        console.error('Error navigating to page:', error);
        setCurrentFile(fileNum);
      }
    },
    [getPageFlip]
  );

  // Next portfolio page (1→2): engine flipPrev — turns from LEFT edge
  const nextPage = useCallback(() => {
    if (currentFile >= TOTAL_PAGES) return;
    const pageFlip = getPageFlip();
    try {
      if (pageFlip?.flipPrev) {
        pageFlip.flipPrev('top');
        return;
      }
      navigateToFile(currentFile + 1);
    } catch (error) {
      console.error('Error flipping next:', error);
      navigateToFile(currentFile + 1);
    }
  }, [currentFile, getPageFlip, navigateToFile]);

  // Previous portfolio page (2→1): engine flipNext — turns from RIGHT edge
  const prevPage = useCallback(() => {
    if (currentFile <= 1) return;
    const pageFlip = getPageFlip();
    try {
      if (pageFlip?.flipNext) {
        pageFlip.flipNext('top');
        return;
      }
      navigateToFile(currentFile - 1);
    } catch (error) {
      console.error('Error flipping prev:', error);
      navigateToFile(currentFile - 1);
    }
  }, [currentFile, getPageFlip, navigateToFile]);

  useEffect(() => {
    const imagePromises = PAGE_FILES.map(
      (n) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = resolve;
          img.src = `/images/page-${n}.png`;
        })
    );
    Promise.all(imagePromises).then(() => setImagesLoaded(true));

    const onResize = () => setBookSize(getBookSize());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        nextPage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        prevPage();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [prevPage, nextPage]);

  return (
    <div className="magazine-viewer">
      <Link to="/" className="floating-back-btn" title="Back to Website">
        ← Back
      </Link>

      <div className="magazine-container">
        {!imagesLoaded ? (
          <div className="magazine-loading">
            <div className="magazine-loading-spinner" />
            <h3>Loading Portfolio</h3>
          </div>
        ) : (
          <div
            className="book-container"
            style={{ width: bookSize.width, height: bookSize.height }}
          >
            <HTMLFlipBook
              key={`${bookSize.width}x${bookSize.height}`}
              ref={bookRef}
              width={bookSize.width}
              height={bookSize.height}
              size="fixed"
              maxShadowOpacity={0.35}
              showCover={false}
              mobileScrollSupport={true}
              onFlip={onFlip}
              onInit={onInit}
              className="flipbook"
              drawShadow={true}
              flippingTime={700}
              usePortrait={true}
              startPage={START_INDEX}
              autoSize={false}
              swipeDistance={30}
              clickEventForward={false}
              useMouseEvents={true}
              showPageCorners={true}
              disableFlipByClick={false}
            >
              {PAGE_FILES.map((fileNum) => (
                <div key={`page-${fileNum}`} className="book-page">
                  <div className="page-content-wrapper">
                    <img
                      src={`/images/page-${fileNum}.png`}
                      alt={`Page ${fileNum}`}
                      className="page-image"
                      draggable="false"
                    />
                  </div>
                </div>
              ))}
            </HTMLFlipBook>
          </div>
        )}
      </div>

      <div className="page-navigation rtl-nav">
        <button
          className="transparent-nav-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            nextPage();
          }}
          disabled={currentFile >= TOTAL_PAGES}
          title="Next Page"
          type="button"
        >
          ←
        </button>

        <div className="bottom-page-indicator">
          <span>
            {currentFile} / {TOTAL_PAGES}
          </span>
          <input
            type="range"
            min={1}
            max={TOTAL_PAGES}
            value={currentFile}
            onChange={(e) => navigateToFile(parseInt(e.target.value, 10))}
            className="bottom-page-slider"
          />
        </div>

        <button
          className="transparent-nav-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            prevPage();
          }}
          disabled={currentFile <= 1}
          title="Previous Page"
          type="button"
        >
          →
        </button>
      </div>

      {showThumbnails && (
        <div className="thumbnails-overlay" onClick={() => setShowThumbnails(false)}>
          <div className="thumbnails-container" onClick={(e) => e.stopPropagation()}>
            <div className="thumbnails-header">
              <h3>Portfolio Pages</h3>
              <button className="close-thumbnails" onClick={() => setShowThumbnails(false)}>
                ×
              </button>
            </div>
            <div className="thumbnails-grid">
              {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map((fileNum) => (
                <div
                  key={fileNum}
                  className={`thumbnail ${currentFile === fileNum ? 'active' : ''}`}
                  onClick={() => navigateToFile(fileNum)}
                >
                  <img
                    src={`/images/page-${fileNum}.png`}
                    alt={`Page ${fileNum}`}
                    className="thumbnail-image"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MagazineViewer;
